import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/card';
import { Button } from '../ui/button'; // Import Button
import { Pencil, Trash2, ChevronDown, ChevronRight, CheckCircle2, Filter, ListTodo } from 'lucide-react'; // Import icons
import { Badge } from '../ui/badge'; // Correct path assumed
import { Goal, Category } from '../../types'; // Import Goal and Category types
import { Checkbox } from "../ui/checkbox"; // Import Checkbox
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { motion, AnimatePresence } from "framer-motion"; // For animations
import { format, isPast, isThisWeek, isThisMonth, isThisYear, parseISO } from 'date-fns'; // Import date-fns functions

interface GoalItemProps {
  goal: Goal;
  allGoals: Goal[]; // Need all goals to find children
  level?: number; // For indentation
  onEditGoal: (goal: Goal) => void; // Edit handler
  expandedGoals: Record<string, boolean>; // Pass state down
  toggleExpand: (goalId: string) => void; // Pass handler down
}

const GoalItem: React.FC<GoalItemProps> = ({ 
    goal, 
    allGoals, 
    level = 0, 
    onEditGoal, 
    expandedGoals, 
    toggleExpand,
}) => {
  // Find sub-goals
  const subGoals = allGoals.filter(g => g.parent_goal === goal.id);
  const isExpanded = expandedGoals[goal.id] ?? true; 
  const hasChildren = subGoals.length > 0;
  const isCompleted = goal.completed_at !== null && goal.completed_at !== undefined;
  
  // Helper function to check if a date is today (ignoring time)
  const isToday = (date: Date | null): boolean => {
    if (!date) return false;
    const today = new Date();
    return (
        date.getDate() === today.getDate() &&
        date.getMonth() === today.getMonth() &&
        date.getFullYear() === today.getFullYear()
    );
  };

  // Check if overdue
  const deadlineDate = goal.deadline ? parseISO(goal.deadline) : null;
  const isOverdue = !isCompleted && deadlineDate && isPast(deadlineDate) && !isToday(deadlineDate); // Check if past and not today

  return (
    <li style={{ marginLeft: `${level * 20}px` }} className="mb-2 list-none"> 
      <Card className={`border ${isCompleted ? "border-green-200 bg-green-50/30 dark:border-green-800 dark:bg-green-950/20" : ""}`}> 
        <CardContent className="p-3"> 
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-start gap-2 flex-1 min-w-0"> 
              {hasChildren && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 p-0 mt-0.5 flex-shrink-0" 
                  aria-label={isExpanded ? 'Collapse goal' : 'Expand goal'} 
                  onClick={() => toggleExpand(goal.id.toString())} 
                >
                  {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </Button>
              )}
              <div className={`flex-1 ${!hasChildren ? 'ml-8' : ''}`}> 
                <div className="flex items-center gap-2">
                  <span className={`font-medium truncate ${isCompleted ? 'line-through text-muted-foreground' : ''}`}>{goal.title}</span> 
                  {isCompleted && <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0" />}
                </div>
                <div className="text-xs text-muted-foreground flex flex-wrap items-center gap-x-2 gap-y-1 mt-1">
                  {goal.category_details?.name && <Badge variant="outline" className="whitespace-nowrap">{goal.category_details.name}</Badge>} 
                  {typeof goal.total_task_count === 'number' && typeof goal.completed_task_count === 'number' && (
                      <Badge variant="secondary" className="whitespace-nowrap flex items-center gap-1">
                          <ListTodo className="h-3 w-3" />
                          {goal.completed_task_count} / {goal.total_task_count} Tasks
                      </Badge>
                  )}
                  {goal.deadline && (
                     <span className={`whitespace-nowrap ${isCompleted ? 'line-through' : ''} ${isOverdue ? 'text-red-600 font-medium' : ''}`}>
                       Due: {format(deadlineDate!, 'PPP')}
                     </span>
                  )}
                  {isCompleted && goal.completed_at && (
                    <span className="whitespace-nowrap text-green-700">
                        Completed: {format(parseISO(goal.completed_at), 'PPP')}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => onEditGoal(goal)}
              aria-label={`Edit goal ${goal.title}`}
              className="h-7 w-7 flex-shrink-0 ml-1" 
            >
              <Pencil className="h-4 w-4" /> 
            </Button>
          </div>
        </CardContent>
      </Card>
      
      {hasChildren && (
        <AnimatePresence initial={false}>
          {isExpanded && (
             <motion.ul
                key="content"
                initial="collapsed"
                animate="open"
                exit="collapsed"
                variants={{
                  open: { opacity: 1, height: "auto" },
                  collapsed: { opacity: 0, height: 0 }
                }}
                transition={{ duration: 0.3, ease: [0.04, 0.62, 0.23, 0.98] }}
                className="mt-1 pl-[44px] border-l-2 border-muted ml-3 list-none"
             >
              {subGoals.map(subGoal => (
                <GoalItem 
                    key={subGoal.id} 
                    goal={subGoal} 
                    allGoals={allGoals} 
                    level={level + 1} 
                    onEditGoal={onEditGoal} 
                    expandedGoals={expandedGoals}
                    toggleExpand={toggleExpand}
                />
              ))}
            </motion.ul>
          )}
        </AnimatePresence>
      )}
    </li>
  );
}

interface GoalTreeProps {
  goals: Goal[]; 
  allGoals: Goal[]; 
  categories: Category[]; 
  onEditGoal: (goal: Goal) => void; 
  onFilterChange: (filter: string) => void; 
  activeFilter: string; 
}

const GoalTree: React.FC<GoalTreeProps> = ({ 
    goals, 
    allGoals, 
    categories, 
    onEditGoal, 
    onFilterChange, 
    activeFilter,
}) => {
  const [expandedGoals, setExpandedGoals] = useState<Record<string, boolean>>({});

  const toggleExpand = (goalId: string) => {
    setExpandedGoals(prev => ({ ...prev, [goalId]: !(prev[goalId] ?? true) }));
  };
  
  const topLevelGoals = goals.filter(g => !g.parent_goal);

  // Helper function to get display text for the filter button
  const getFilterDisplayText = () => {
    if (activeFilter === 'all') return 'All';
    if (activeFilter === 'active') return 'Active';
    if (activeFilter === 'completed') return 'Completed';
    if (activeFilter === 'overdue') return 'Overdue';
    if (activeFilter === 'due_week') return 'Due This Week';
    if (activeFilter === 'due_month') return 'Due This Month';
    if (activeFilter === 'due_year') return 'Due This Year';
    if (activeFilter.startsWith('category:')) {
      const catId = parseInt(activeFilter.split(':')[1]);
      const cat = categories.find(c => c.id === catId);
      return cat ? `Category: ${cat.name}` : 'Category';
    }
    return 'All'; // Fallback
  };

  return (
     <div className="space-y-4">
       <div className="flex justify-between items-center">
         <h3 className="text-lg font-medium">Goal Hierarchy</h3>
         <DropdownMenu>
           <DropdownMenuTrigger asChild>
             <Button variant="outline" size="sm" className="flex items-center gap-2">
               <Filter className="h-4 w-4" />
               <span>Filter: {getFilterDisplayText()}</span>
             </Button>
           </DropdownMenuTrigger>
           <DropdownMenuContent align="end" className="w-56">
             <DropdownMenuLabel>Filter Goals</DropdownMenuLabel>
             <DropdownMenuSeparator />
             <DropdownMenuGroup>
               <DropdownMenuItem onClick={() => onFilterChange("all")}>
                 <span className={activeFilter === "all" ? "font-bold" : ""}>All Goals</span>
               </DropdownMenuItem>
               <DropdownMenuItem onClick={() => onFilterChange("active")}>
                 <span className={activeFilter === "active" ? "font-bold" : ""}>Active Goals</span>
               </DropdownMenuItem>
               <DropdownMenuItem onClick={() => onFilterChange("completed")}>
                 <span className={activeFilter === "completed" ? "font-bold" : ""}>Completed Goals</span>
               </DropdownMenuItem>
             </DropdownMenuGroup>
             <DropdownMenuSeparator />
             <DropdownMenuLabel>By Deadline</DropdownMenuLabel>
             <DropdownMenuGroup>
               <DropdownMenuItem onClick={() => onFilterChange("overdue")}>Overdue</DropdownMenuItem>
               <DropdownMenuItem onClick={() => onFilterChange("due_week")}>Due This Week</DropdownMenuItem>
               <DropdownMenuItem onClick={() => onFilterChange("due_month")}>Due This Month</DropdownMenuItem>
               <DropdownMenuItem onClick={() => onFilterChange("due_year")}>Due This Year</DropdownMenuItem>
             </DropdownMenuGroup>
             <DropdownMenuSeparator />
             <DropdownMenuLabel>By Category</DropdownMenuLabel>
             {categories.map((category) => (
               <DropdownMenuItem key={category.id} onClick={() => onFilterChange(`category:${category.id}`)}>
                 <span className={activeFilter === `category:${category.id}` ? "font-bold" : ""}>{category.name}</span>
               </DropdownMenuItem>
             ))}
           </DropdownMenuContent>
         </DropdownMenu>
       </div>
       
       <Card>
         <CardContent className="pt-6"> 
          {topLevelGoals.length === 0 ? (
              <p className="text-center text-muted-foreground py-4">
                No goals match your filter.
              </p>
           ) : (
             <ul className="list-none pl-0 space-y-1">
              {topLevelGoals.map(goal => (
                <GoalItem 
                    key={goal.id} 
                    goal={goal} 
                    allGoals={allGoals} 
                    onEditGoal={onEditGoal} 
                    expandedGoals={expandedGoals} 
                    toggleExpand={toggleExpand} 
                />
              ))}
            </ul>
           )}
         </CardContent>
      </Card>
    </div>
  );
};

export default GoalTree; 